import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import type { User, Session } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase/client'
import { clearSupabaseAuthStorage } from '@/lib/supabase/clearAuthStorage'

// ─── Types ───────────────────────────────────────────────────────────────────

export type AdminRole = 'super_admin' | 'admin' | 'support'

const VALID_ROLES: AdminRole[] = ['super_admin', 'admin', 'support']

interface AdminAuthContextValue {
  user: User | null
  session: Session | null
  adminRole: AdminRole | null
  isLoading: boolean
  isAuthenticated: boolean
  isAuthorized: boolean
  signIn: (email: string, password: string) => Promise<{ error: string | null }>
  signOut: () => Promise<void>
}

// ─── Context ─────────────────────────────────────────────────────────────────

const AdminAuthContext = createContext<AdminAuthContextValue | null>(null)

function extractAdminRole(user: User | null): AdminRole | null {
  if (!user) return null
  const role = (user.app_metadata as Record<string, unknown>)?.role as string | undefined
  return VALID_ROLES.includes(role as AdminRole) ? (role as AdminRole) : null
}

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const userIdRef = useRef<string | null>(null)

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      if (s?.user && s.user.id !== userIdRef.current) {
        userIdRef.current = s.user.id
        setUser(s.user)
        setSession(s)
      }
      setIsLoading(false)
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, s) => {
      const newId = s?.user?.id ?? null
      if (newId !== userIdRef.current) {
        userIdRef.current = newId
        setUser(s?.user ?? null)
        setSession(s)
        if (!s?.user) {
          // cleared on logout
        }
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  const signIn = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password })
      if (error) return { error: error.message }

      // Check admin role immediately after sign-in
      const role = extractAdminRole(data.user)
      if (!role) {
        await supabase.auth.signOut()
        return { error: 'Access denied. This account does not have admin privileges.' }
      }

      return { error: null }
    } catch (e) {
      return { error: e instanceof Error ? e.message : 'Sign in failed' }
    }
  }

  const signOut = async () => {
    try {
      await supabase.auth.signOut()
    } catch {
      /* fall through to forced purge below */
    }
    // supabase-js leaves the persisted token in place when the server logout
    // request fails (network/5xx); purge it so the session can't auto-recover.
    clearSupabaseAuthStorage()
    userIdRef.current = null
    setUser(null)
    setSession(null)
  }

  const adminRole = extractAdminRole(user)

  return (
    <AdminAuthContext.Provider
      value={{
        user,
        session,
        adminRole,
        isLoading,
        isAuthenticated: !!user,
        isAuthorized: !!user && !!adminRole,
        signIn,
        signOut,
      }}
    >
      {children}
    </AdminAuthContext.Provider>
  )
}

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext)
  if (!ctx) throw new Error('useAdminAuth must be used within AdminAuthProvider')
  return ctx
}
